import { useCallback, useEffect, useMemo, useState } from "react";

import { listarEventos, listarUsuarios, type EventoCrudo, type Yo } from "./api";
import { Cargando, ErrorDeCarga, Pantalla, Tabla, type Columna } from "./componentes";
import { fraseDeAccion, fraseDeEntidad } from "./eventos-texto";
import { fechaHora, haceCuanto } from "./formato";
import { mensajeDeError } from "./mensajes";
import { sinAcentos } from "./texto";

/**
 * ACTIVIDAD: la bitácora, leída. El análisis de la v1 lo midió: 559 renglones
 * registrados y CERO lectores en el código — se registraba para nadie. Aquí
 * el flujo de eventos (D018) tiene por fin quien lo mire: quién hizo qué,
 * sobre qué, cuándo y desde dónde, con filtro por acción y por texto.
 *
 * Los nombres salen de /usuarios (solo si la sesión administra cuentas); si
 * no, se ve el id, que es la verdad disponible.
 */
const POR_PAGINA = 200;
const TOPE_PAGINAS = 10;

export function Actividad({ yo }: { yo: Yo }) {
  const [eventos, setEventos] = useState<EventoCrudo[] | null>(null);
  const [nombres, setNombres] = useState<Map<number, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const cargar = useCallback(async () => {
    setError(null);
    try {
      // El generador lista en orden de llegada y no sabe «lo último», así
      // que se traen las páginas que haya (con un tope) y se invierten aquí.
      // Para una campaña con decenas de miles de eventos habrá que pedirle
      // un orden al servidor; hoy el tope basta y está dicho.
      const filas: EventoCrudo[] = [];
      for (let pagina = 0; pagina < TOPE_PAGINAS; pagina++) {
        const r = await listarEventos(POR_PAGINA, pagina * POR_PAGINA);
        filas.push(...r.filas);
        if (r.filas.length < POR_PAGINA) break;
      }
      setEventos([...filas].sort((a, b) => b.id - a.id));
      if (yo.capacidades["administrar_usuarios"]) {
        const u = await listarUsuarios(200, 0);
        setNombres(new Map(u.filas.map((x) => [x.id, x.nombre || x.usuario])));
      }
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, [yo.capacidades]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const acciones = useMemo(
    () => [...new Set((eventos ?? []).map((e) => e.accion))].sort((a, b) => a.localeCompare(b, "es")),
    [eventos],
  );

  const visibles = useMemo(() => {
    if (!eventos) return [];
    const q = sinAcentos(busqueda.trim());
    return eventos.filter((e) => {
      if (accion && e.accion !== accion) return false;
      if (!q) return true;
      const quien = e.usuario_id != null ? (nombres.get(e.usuario_id) ?? String(e.usuario_id)) : "";
      return sinAcentos(
        [quien, fraseDeAccion(e.accion), e.entidad ?? "", e.entidad_id ?? "", JSON.stringify(e.detalle)].join(" "),
      ).includes(q);
    });
  }, [eventos, accion, busqueda, nombres]);

  if (error) {
    return (
      <Pantalla titulo="Actividad">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }
  if (!eventos) {
    return (
      <Pantalla titulo="Actividad">
        <Cargando que="la bitácora" />
      </Pantalla>
    );
  }

  const columnas: Columna<EventoCrudo>[] = [
    {
      id: "cuando",
      titulo: "Cuándo",
      clave: (e) => e.id,
      celda: (e) => (
        <>
          {haceCuanto(e.ocurrido)}
          <br />
          <span className="apagado chico">{fechaHora(e.ocurrido)}</span>
        </>
      ),
    },
    {
      id: "quien",
      titulo: "Quién",
      clave: (e) => (e.usuario_id != null ? (nombres.get(e.usuario_id) ?? String(e.usuario_id)) : ""),
      celda: (e) =>
        e.usuario_id == null ? (
          <span className="apagado">sin sesión</span>
        ) : (
          <strong>{nombres.get(e.usuario_id) ?? `cuenta ${e.usuario_id}`}</strong>
        ),
    },
    {
      id: "que",
      titulo: "Qué",
      clave: (e) => e.accion,
      celda: (e) => (
        <>
          {fraseDeAccion(e.accion)}
          {e.entidad && <span className="apagado"> · {fraseDeEntidad(e.entidad, e.entidad_id)}</span>}
        </>
      ),
    },
    {
      id: "detalle",
      titulo: "Detalle",
      celda: (e) =>
        Object.keys(e.detalle ?? {}).length === 0 ? (
          <span className="apagado">—</span>
        ) : (
          <code className="detalle-evento">
            {Object.entries(e.detalle)
              .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
              .join(" · ")}
          </code>
        ),
    },
    {
      id: "desde",
      titulo: "Desde",
      celda: (e) => <span className="apagado chico">{e.ip ?? "—"}</span>,
    },
  ];

  return (
    <Pantalla
      titulo="Actividad"
      descripcion="Todo lo que cambia deja un renglón aquí, y nadie puede editarlo ni borrarlo."
    >
      <div className="herramientas">
        <label className="solo-lectores" htmlFor="buscar-eventos">
          Buscar en la bitácora
        </label>
        <input
          id="buscar-eventos"
          className="buscar"
          type="search"
          placeholder="Buscar por quién, qué o detalle"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <label className="solo-lectores" htmlFor="filtro-accion">
          Filtrar por acción
        </label>
        <select id="filtro-accion" className="filtro" value={accion} onChange={(e) => setAccion(e.target.value)}>
          <option value="">Todas las acciones</option>
          {acciones.map((a) => (
            <option key={a} value={a}>
              {fraseDeAccion(a)}
            </option>
          ))}
        </select>
        <span className="cuenta">
          {visibles.length === eventos.length
            ? `${eventos.length} renglones`
            : `${visibles.length} de ${eventos.length} renglones`}
        </span>
      </div>
      <Tabla
        columnas={columnas}
        filas={visibles}
        claveDe={(e) => e.id}
        caption="La bitácora de la campaña, lo último primero."
        vacio={busqueda || accion ? "Nada coincide con el filtro." : "Todavía no pasa nada."}
      />
    </Pantalla>
  );
}
